import { isAppAdmin } from '@ordo/auth'
import {
  AppSidebar,
  AppSidebarAdminGroup,
  AppSidebarAppsList,
  AppSidebarAppSwitcher,
  AppSidebarFooter,
  AppSidebarItem,
  AppSidebarNav,
  AppSidebarRegularGroup,
  AppSidebarSection,
  AppSidebarUserMenu,
} from '@ordo/ui/app-sidebar'
import { CommunicationsLogo } from '@ordo/ui/logos'
import { useTheme } from 'hooks/useTheme'
import { Home, type LucideIcon } from 'lucide-react'
import { createElement, useCallback } from 'react'
import { Link, useLocation, useNavigate } from 'react-router'

import { navGroup } from '../lib/nav'
import { SETTINGS_NAV } from '../lib/settings-nav'
import type { User } from '../types/users'

interface SidebarProps {
  user: User
  variant?: 'desktop' | 'drawer'
  onClose?: () => void
}

function navIcon(entry: { icon: LucideIcon }) {
  return createElement(entry.icon)
}

const CONTACT_ITEMS = navGroup('sidebar', 'contacts')
const CONTENT_ITEMS = navGroup('sidebar', 'content')
const COMMS_ITEMS = navGroup('sidebar', 'communications')

function isActive(pathname: string, to: string): boolean {
  return pathname === to || pathname.startsWith(`${to}/`)
}

export function Sidebar({ user, variant, onClose }: SidebarProps) {
  const { pathname } = useLocation()
  const navigate = useNavigate()
  const { preference: colorMode, setPreference: setColorMode } = useTheme(user)
  const resolvedVariant = variant ?? (onClose ? 'drawer' : 'desktop')

  const handleLogout = useCallback(async () => {
    await fetch('/api/auth/logout', { method: 'DELETE' })
    navigate('/login')
  }, [navigate])

  return (
    <AppSidebar variant={resolvedVariant} onNavigate={onClose}>
      <AppSidebarAppSwitcher
        appIcon={<CommunicationsLogo />}
        appName="Communications"
        appColorKey="purple"
        currentAppId="comms"
        renderHomeLink={(children) => (
          <Link to="/" className="block">
            {children}
          </Link>
        )}
      />

      <AppSidebarNav>
        <AppSidebarAppsList
          currentAppId="comms"
          hideCurrent
          renderAppLink={(app, children) =>
            app.href.startsWith('http') ? (
              <a href={app.href} className="block">
                {children}
              </a>
            ) : (
              <Link to={app.href} className="block">
                {children}
              </Link>
            )
          }
        />
        <AppSidebarSection>
          <AppSidebarItem asChild active={pathname === '/'} icon={<Home />}>
            <Link to="/">Home</Link>
          </AppSidebarItem>
        </AppSidebarSection>

        <AppSidebarRegularGroup label="Contacts">
          {CONTACT_ITEMS.map((item) => (
            <AppSidebarItem asChild key={item.href} active={isActive(pathname, item.href)} icon={navIcon(item)}>
              <Link to={item.href}>{item.label}</Link>
            </AppSidebarItem>
          ))}
        </AppSidebarRegularGroup>

        <AppSidebarRegularGroup label="Content">
          {CONTENT_ITEMS.map((item) => (
            <AppSidebarItem asChild key={item.href} active={isActive(pathname, item.href)} icon={navIcon(item)}>
              <Link to={item.href}>{item.label}</Link>
            </AppSidebarItem>
          ))}
        </AppSidebarRegularGroup>

        <AppSidebarRegularGroup label="Communications">
          {COMMS_ITEMS.map((item) => (
            <AppSidebarItem asChild key={item.href} active={isActive(pathname, item.href)} icon={navIcon(item)}>
              <Link to={item.href}>{item.label}</Link>
            </AppSidebarItem>
          ))}
        </AppSidebarRegularGroup>

        {isAppAdmin(user, 'comms') && (
          <AppSidebarAdminGroup label="Settings">
            {SETTINGS_NAV.map((item) => (
              <AppSidebarItem asChild key={item.href} active={isActive(pathname, item.href)} icon={navIcon(item)}>
                <Link to={item.href}>{item.label}</Link>
              </AppSidebarItem>
            ))}
          </AppSidebarAdminGroup>
        )}
      </AppSidebarNav>

      <AppSidebarFooter>
        <AppSidebarUserMenu
          name={user.name}
          email={user.email}
          imageSrc={user.picture}
          colorMode={colorMode}
          onColorModeChange={setColorMode}
          onProfile={() => navigate('/settings')}
          onSignOut={handleLogout}
        />
      </AppSidebarFooter>
    </AppSidebar>
  )
}
